import { Suspense, lazy, useState, type ReactNode } from "react";
import { useT } from "../i18n/LanguageProvider";

// Lazy-loaded so the decoder is split into its own chunk and only
// downloaded when the user actually opens the scanner.
const BarcodeScanner = lazy(() =>
  import("./BarcodeScanner").then((m) => ({ default: m.BarcodeScanner })),
);

interface BarcodeFieldProps {
  id: string;
  value: string;
  onChange: (next: string) => void;
  /** Defaults to the translated word "Barcode". */
  label?: string;
  maxLength?: number;
  /** Optional hint text rendered under the input. */
  hint?: string;
  /** Optional fast path used by register screens that act immediately after a camera scan. */
  onScanDetected?: (code: string) => void;
  /**
   * Rendered on the input ROW, beside Scan. Use this for a host's own submit
   * button instead of placing it next to the whole field, which would align it
   * to the bottom of the hint rather than to the input.
   */
  actions?: ReactNode;
}

/**
 * Barcode text input paired with a "Scan" button that opens the camera
 * scanner overlay and fills the field with the decoded value.
 */
export function BarcodeField({
  id,
  value,
  onChange,
  label,
  maxLength = 64,
  hint,
  onScanDetected,
  actions,
}: BarcodeFieldProps) {
  const { t } = useT();
  const [scanning, setScanning] = useState(false);
  const fieldLabel = label ?? t("itemCard.barcode");

  return (
    <div className="field">
      <label htmlFor={id}>{fieldLabel}</label>
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "stretch" }}>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          maxLength={maxLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // min-width 0: a text input's intrinsic width (~170px) otherwise
          // wins over flex shrinking and pushes the Scan button out of any
          // narrow host (owner screenshot 2026-08-09, inline-sell finder).
          style={{ flex: 1, minWidth: 0 }}
        />
        <button
          type="button"
          className="button tinted"
          onClick={() => setScanning(true)}
          aria-label={t("barcode.scanWithCamera", { what: fieldLabel.toLowerCase() })}
        >
          {t("itemFinder.scan")}
        </button>
        {/*
          Anything the host wants ON the input row, beside Scan.

          Owner 2026-09-11: "scan and 'find line' are not on the same line,
          breaking design". A host that rendered its own button as a SIBLING of
          this whole field got it aligned to the bottom of the field — which
          includes the hint line under the input — so the button sat a row lower
          than Scan. Passing it here puts it where it belongs.
        */}
        {actions}
      </div>
      {hint ? <p className="field-hint">{hint}</p> : null}
      {scanning ? (
        <Suspense fallback={null}>
          <BarcodeScanner
            onDetected={(code) => {
              onChange(code);
              setScanning(false);
              onScanDetected?.(code);
            }}
            onClose={() => setScanning(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
